import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import tokenStore from "./tokenStore";
import { getSessionConfig } from "./sessionConfig";
import { refreshSession } from "./refreshManager";
import { useInactivityTimer } from "./useInactivityTimer";
import {
  subscribeSessionMessages,
} from "./sessionChannel";
import {
  performSignOut,
  performLocalSignOut,
} from "./performSignOut";
import SessionExpiryModal from "../components/SessionExpiryModal";

// Mounted once in App. Self-gating: renders/tracks nothing without a session
// cookie or on the login route. Owns the inactivity timer, the expiry modal
// and the cross-tab subscription.

const SessionManager: React.FC = () => {
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const signingOutRef = useRef(false);

  const enabled = tokenStore.hasSession() && location.pathname !== "/";
  const { idleTimeoutSec, warningBeforeSec } = getSessionConfig();

  const timer = useInactivityTimer({ enabled, idleTimeoutSec, warningBeforeSec });
  const { state, remainingSec, reset, notifyExternalActivity } = timer;

  // Cross-tab: logout anywhere logs this tab out; activity/refresh anywhere
  // keeps this tab's session alive (an active sibling vouches for the user).
  useEffect(() => {
    if (!enabled) return;
    return subscribeSessionMessages((msg) => {
      if (msg.type === "LOGOUT") {
        if (!signingOutRef.current) {
          signingOutRef.current = true;
          performLocalSignOut(msg.reason);
        }
      } else {
        notifyExternalActivity(msg.at);
      }
    });
  }, [enabled, notifyExternalActivity]);

  // Countdown reached zero → revoke and leave with the "session expired" message.
  useEffect(() => {
    if (enabled && state === "expired" && !signingOutRef.current) {
      signingOutRef.current = true;
      performSignOut("expired");
    }
  }, [enabled, state]);

  const handleStayLoggedIn = async () => {
    setBusy(true);
    try {
      // No reload, no navigation: mint a fresh access token and rearm the
      // timer — whatever the user was doing stays exactly as it was.
      await refreshSession();
      reset();
    } catch {
      if (!signingOutRef.current) {
        signingOutRef.current = true;
        await performSignOut("expired");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    setBusy(true);
    await performSignOut("user");
  };

  if (!enabled) return null;

  return (
    <SessionExpiryModal
      isOpen={state === "warning"}
      remainingSec={remainingSec}
      busy={busy}
      onStayLoggedIn={handleStayLoggedIn}
      onSignOut={handleSignOut}
    />
  );
};

export default SessionManager;
